// Мелкие форматтеры страницы «Сцена» (отдельно от компонентов — react-refresh).

export function plural(n: number, forms: [string, string, string]): string {
	const m10 = n % 10, m100 = n % 100
	if (m10 === 1 && m100 !== 11) return forms[0]
	if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1]
	return forms[2]
}

/** Секунды с одним знаком и запятой, как принято по-русски: «3,5 с». */
export function seconds(v: number | null | undefined): string {
	if (v == null || !Number.isFinite(v)) return '—'
	return `${String(Math.round(v * 10) / 10).replace('.', ',')} с`
}

/** Время сервера → миллисекунды. Строку без пояса читаем как UTC (так пишет бэк), а не как местное время. */
export function serverTime(iso: string | null | undefined): number {
	if (!iso) return Number.NaN
	return new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`).getTime()
}

/** «5 минут назад» / «вчера» / дата — как «Last opened 25 weeks ago» у примера Artlist. */
export function ago(iso: string | null | undefined): string {
	if (!iso) return ''
	const t = serverTime(iso)
	if (!Number.isFinite(t)) return ''
	const diff = Math.max(0, Date.now() - t)
	const min = Math.round(diff / 60000)
	if (min < 1) return 'только что'
	if (min < 60) return `${min} ${plural(min, ['минуту', 'минуты', 'минут'])} назад`
	const h = Math.round(min / 60)
	if (h < 24) return `${h} ${plural(h, ['час', 'часа', 'часов'])} назад`
	const d = Math.round(h / 24)
	if (d < 30) return `${d} ${plural(d, ['день', 'дня', 'дней'])} назад`
	const w = Math.round(d / 7)
	if (d < 90) return `${w} ${plural(w, ['неделю', 'недели', 'недель'])} назад`
	return new Date(t).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** «30 сент., 08:36» — местное время пользователя. */
export function dateTime(iso: string | null | undefined): string {
	const t = serverTime(iso)
	if (!Number.isFinite(t)) return ''
	return new Date(t).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/** Срок хранения версии словами (истина — expires_at файла). Округляем вниз: «ещё 2 дня» значит, что два полных
 *  дня точно есть (раньше округляли вверх — файл, которому оставался час, «хранился до завтра»). */
export function keepNote(expiresAt: string | null | undefined): string {
	if (!expiresAt) return ''
	const t = serverTime(expiresAt)
	if (!Number.isFinite(t)) return ''
	const ms = t - Date.now()
	if (ms <= 0) return 'срок хранения истёк'
	const hours = Math.floor(ms / 3600000)
	if (hours < 1) return 'хранится меньше часа'
	if (hours < 24) return `хранится ещё ${hours} ${plural(hours, ['час', 'часа', 'часов'])}`
	const days = Math.floor(hours / 24)
	return `хранится ещё ${days} ${plural(days, ['день', 'дня', 'дней'])}`
}

/** «5 секунд» — словами, там, где «5 с» читается как предлог («на 5 с той же нейросетью»). */
export function secondsWord(n: number): string {
	return `${n} ${plural(n, ['секунду', 'секунды', 'секунд'])}`
}

export const TOUR_KEY = 'umnik.scene.tour.done'

/** Тур уже показан на этом устройстве (без localStorage — считаем показанным). */
export function tourDoneFor(key: string): boolean {
	try { return localStorage.getItem(key) === '1' } catch { return true }
}

export function tourDone(): boolean {
	try { return localStorage.getItem(TOUR_KEY) === '1' } catch { return true }
}

/** «16:9» → 1.777… (ширина к высоте). Непонятное значение (adaptive, auto, пусто) → null. */
export function parseAspect(ar: unknown): number | null {
	if (typeof ar !== 'string') return null
	const m = /^(\d+(?:\.\d+)?)\s*[:/x×]\s*(\d+(?:\.\d+)?)$/.exec(ar.trim())
	if (!m) return null
	const w = Number(m[1]), h = Number(m[2])
	return w > 0 && h > 0 ? w / h : null
}

/** Пропорция карточки холста в рабочих границах: вытянутее 21:9 и уже 9:21 не рисуем — холст развалится. */
export function clampRatio(r: number | null | undefined, fallback = 16 / 9): number {
	if (!r || !Number.isFinite(r)) return fallback
	return Math.min(21 / 9, Math.max(9 / 21, r))
}

/** Имя файла для скачивания: без символов, которые файловые системы не принимают. */
export function fileName(title: string, suffix: string, ext: string): string {
	const clean = (title || '').replace(/[\\/:*?"<>|\n\r\t]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)
	const base = clean ? `${clean} — ${suffix}` : suffix
	return `${base}.${ext}`
}

/** Расширение файла по ссылке (presigned — с query): .png/.jpg/.webp/.mp4…; непонятно — запасное. */
export function extOf(url: string | null | undefined, fallback: string): string {
	if (!url) return fallback
	const m = /\.([a-z0-9]{2,5})(?:$|[?#])/i.exec(url.split('?')[0] + '?')
	return m ? m[1].toLowerCase() : fallback
}

// Свои ключи localStorage (чужие не трогаем): свёрнута ли панель шота и панель шотов.
export const PANEL_KEY = 'umnik.scene.panel.collapsed'
export const SHOTS_KEY = 'umnik.scene.shots.collapsed'

export function readFlag(key: string): boolean {
	try { return localStorage.getItem(key) === '1' } catch { return false }
}

export function writeFlag(key: string, on: boolean): void {
	try { if (on) localStorage.setItem(key, '1'); else localStorage.removeItem(key) } catch { /* без хранилища — просто не запомним */ }
}

/** Откуда версия — словами: для подписи под холстом и подсказки в истории. */
export function sourceLabel(v: { source: string; settings?: Record<string, unknown> | null }, readonly: boolean, modelLabel?: string | null): string {
	if (v.source === 'uploaded') return readonly ? 'Загружено автором примера' : 'Загружено вами'
	if (v.source === 'extracted') return v.settings?.from_video ? 'Кадр из видео другого шота' : 'Кадр из другого шота'
	return modelLabel ? `Создано: ${modelLabel}` : 'Создано нейросетью'
}

/** Время как у InShot: «0:05.1» — минуты, секунды и десятые. */
export function clock(v: number | null | undefined): string {
	if (v == null || !Number.isFinite(v)) return '—'
	const t = Math.max(0, Math.round(v * 10) / 10)
	const m = Math.floor(t / 60)
	const s = t - m * 60
	return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`
}

/** Подпись деления линейки монтажа: «00:02». */
export function rulerLabel(v: number): string {
	const t = Math.max(0, Math.round(v))
	return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
}
